import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { GraduationCap, HeartPulse, FileCheck2, Eye } from "lucide-react";
import { Reveal } from "./Reveal";

const TRAINING_IMG =
  "https://images.unsplash.com/photo-1622612023350-b15f063eabe6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxzYWZldHklMjB0cmFpbmluZyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3ODQxNDg0Njh8MA&ixlib=rb-4.1.0&q=85";

const POINTS = [
  { icon: GraduationCap, title: "Certified & Continuous Training", desc: "Every guard is licensed and re-trained regularly on protocol, de-escalation and site-specific procedures." },
  { icon: HeartPulse, title: "Safety First — Always", desc: "Safety is our highest priority. First-aid ready teams and strict health & safety standards on every deployment." },
  { icon: FileCheck2, title: "Compliance & Reporting", desc: "Detailed incident logs and transparent reporting keep you informed and audit-ready at all times." },
  { icon: Eye, title: "Vigilance by Design", desc: "Proactive observation and clear escalation paths stop incidents before they become emergencies." },
];

export const Training = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="training" data-testid="training-section" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div ref={ref} className="relative order-2 lg:order-1">
          <div className="relative overflow-hidden rounded-sm border border-white/10 aspect-[4/5]">
            <motion.img
              style={{ y }}
              src={TRAINING_IMG}
              alt="Safety-trained security professional"
              className="w-full h-[115%] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          </div>
          <div className="absolute -bottom-6 -right-4 md:right-6 bg-[color:var(--primary)] text-white p-6 rounded-sm glow-red max-w-[220px]">
            <div className="font-display text-4xl leading-none">100%</div>
            <div className="mt-1 text-sm font-medium opacity-90">
              Safety-first commitment on every site, every shift.
            </div>
          </div>
        </div>

        <div className="order-1 lg:order-2">
          <Reveal>
            <span className="text-xs font-bold uppercase tracking-[0.24em] text-[color:var(--primary-text)]">
              Training &amp; Safety
            </span>
            <h2 className="mt-5 font-display text-4xl md:text-6xl tracking-tight leading-none">
              Our highest{" "}
              <span className="font-editorial italic">priority</span>.
            </h2>
            <p className="mt-6 text-lg text-secondary leading-relaxed max-w-xl">
              A guard is only as good as their preparation. We hold our people to
              elite standards so you get protection you can rely on.
            </p>
          </Reveal>

          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            {POINTS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.title} delay={i * 0.07}>
                  <div
                    data-testid={`training-point-${i}`}
                    className="h-full bg-[color:var(--surface)] border border-white/10 rounded-sm p-6 transition-colors hover:border-[color:var(--primary)]/40"
                  >
                    <Icon size={24} className="text-[color:var(--primary-text)]" />
                    <h3 className="mt-4 font-display text-lg tracking-tight">{p.title}</h3>
                    <p className="mt-2 text-sm text-secondary leading-relaxed">{p.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
